import { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, FileText, Loader2, Package, Plus, Receipt, RefreshCw, Search, Settings } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { notasFiscaisApi, formatarDocumento, formatarMoeda, mensagemErro } from '@/lib/notasFiscaisApi';
import {
  STATUS_NOTA_CLASSE,
  STATUS_NOTA_LABEL,
  type ConfigFiscal,
  type NotaFiscal,
  type NotaFiscalResumo,
  type ProdutoRevenda,
  type RegraFiscal,
  type StatusIntegracaoFiscal,
  type StatusNotaFiscal,
} from '@/types/notasFiscais';
import { NotaFiscalFormDialog } from '@/components/notas-fiscais/NotaFiscalFormDialog';
import { NotaFiscalDetalheDialog } from '@/components/notas-fiscais/NotaFiscalDetalheDialog';
import { ProdutosRevendaTab } from '@/components/notas-fiscais/ProdutosRevendaTab';
import { ConfiguracaoFiscalTab } from '@/components/notas-fiscais/ConfiguracaoFiscalTab';

export default function NotasFiscais() {
  const { toast } = useToast();
  const [status, setStatus] = useState<StatusIntegracaoFiscal | null>(null);
  const [config, setConfig] = useState<ConfigFiscal | null>(null);
  const [regras, setRegras] = useState<RegraFiscal[]>([]);
  const [produtos, setProdutos] = useState<ProdutoRevenda[]>([]);
  const [notas, setNotas] = useState<NotaFiscalResumo[]>([]);
  const [carregandoNotas, setCarregandoNotas] = useState(true);
  const [carregandoProdutos, setCarregandoProdutos] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<StatusNotaFiscal | 'todas'>('todas');
  const [busca, setBusca] = useState('');
  const [formAberto, setFormAberto] = useState(false);
  const [notaEditando, setNotaEditando] = useState<string | null>(null);
  const [notaBase, setNotaBase] = useState<string | null>(null);
  const [notaDetalhe, setNotaDetalhe] = useState<string | null>(null);

  const carregarNotas = useCallback(async () => {
    setCarregandoNotas(true);
    try {
      setNotas(await notasFiscaisApi.listarNotas());
    } catch (e) {
      toast({ title: 'Erro ao carregar notas', description: mensagemErro(e), variant: 'destructive' });
    } finally {
      setCarregandoNotas(false);
    }
  }, [toast]);

  const carregarCadastros = useCallback(async () => {
    setCarregandoProdutos(true);
    try {
      const [s, c, r, p] = await Promise.all([
        notasFiscaisApi.status(),
        notasFiscaisApi.obterConfig(),
        notasFiscaisApi.listarRegras(),
        notasFiscaisApi.listarProdutos(),
      ]);
      setStatus(s);
      setConfig(c);
      setRegras(r);
      setProdutos(p);
    } catch (e) {
      toast({ title: 'Erro ao carregar configuração fiscal', description: mensagemErro(e), variant: 'destructive' });
    } finally {
      setCarregandoProdutos(false);
    }
  }, [toast]);

  useEffect(() => {
    carregarNotas();
    carregarCadastros();
  }, [carregarNotas, carregarCadastros]);

  const notasFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return notas.filter((n) => {
      if (filtroStatus !== 'todas' && n.status !== filtroStatus) return false;
      if (!termo) return true;
      return [n.numero, n.destinatario?.nome, n.destinatario?.cpf, n.destinatario?.cnpj, n.chave_nfe].some((v) =>
        String(v || '').toLowerCase().includes(termo)
      );
    });
  }, [notas, filtroStatus, busca]);

  const abrirNova = () => {
    setNotaEditando(null);
    setNotaBase(null);
    setFormAberto(true);
  };

  const abrirEdicao = (id: string) => {
    setNotaDetalhe(null);
    setNotaBase(null);
    setNotaEditando(id);
    setFormAberto(true);
  };

  const usarComoBase = (id: string) => {
    setNotaDetalhe(null);
    setNotaEditando(null);
    setNotaBase(id);
    setFormAberto(true);
  };

  const aoAtualizarNota = useCallback(() => {
    carregarNotas();
    carregarCadastros();
  }, [carregarNotas, carregarCadastros]);

  const aoSalvar = (nota: NotaFiscal, { emitida, manterAberto }: { emitida: boolean; manterAberto: boolean }) => {
    carregarNotas();
    if (emitida) carregarCadastros();
    if (manterAberto) {
      setNotaEditando(nota.id);
      return;
    }
    setFormAberto(false);
    if (emitida) setNotaDetalhe(nota.id);
  };

  const produtosAtivos = useMemo(() => produtos.filter((p) => p.ativo), [produtos]);
  const ambiente = config?.ambiente ?? 'homologacao';

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            Notas Fiscais
            {ambiente === 'homologacao' ? (
              <Badge variant="secondary">Homologação</Badge>
            ) : (
              <Badge variant="destructive">Produção</Badge>
            )}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {ambiente === 'producao'
              ? 'NF-e com valor fiscal, enviada à SEFAZ - Sun7 Energy Comercial LTDA'
              : 'NF-e de teste, sem valor fiscal - Sun7 Energy Comercial LTDA, via Focus NFe'}
          </p>
        </div>
        <Button onClick={abrirNova}><Plus className="h-4 w-4 mr-1" /> Nova NF-e</Button>
      </div>

      <Tabs defaultValue="notas">
        <TabsList>
          <TabsTrigger value="notas" className="gap-2"><Receipt className="h-4 w-4" /> Notas</TabsTrigger>
          <TabsTrigger value="produtos" className="gap-2"><Package className="h-4 w-4" /> Produtos</TabsTrigger>
          <TabsTrigger value="configuracao" className="gap-2"><Settings className="h-4 w-4" /> Configuração</TabsTrigger>
        </TabsList>

        <TabsContent value="notas">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex flex-col md:flex-row gap-2">
                <div className="relative md:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    placeholder="Número, cliente, CPF/CNPJ ou chave"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                  />
                </div>
                <Select value={filtroStatus} onValueChange={(v) => setFiltroStatus(v as StatusNotaFiscal | 'todas')}>
                  <SelectTrigger className="md:w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todos os status</SelectItem>
                    {(Object.keys(STATUS_NOTA_LABEL) as StatusNotaFiscal[]).map((s) => (
                      <SelectItem key={s} value={s}>{STATUS_NOTA_LABEL[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={carregarNotas} disabled={carregandoNotas}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${carregandoNotas ? 'animate-spin' : ''}`} /> Atualizar
                </Button>
              </div>

              {carregandoNotas ? (
                <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
              ) : notasFiltradas.length === 0 ? (
                <p className="text-center text-muted-foreground py-10">Nenhuma nota encontrada.</p>
              ) : (
                <div className="border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Número</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Destinatário</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {notasFiltradas.map((n) => (
                        <TableRow key={n.id} className="cursor-pointer" onClick={() => setNotaDetalhe(n.id)}>
                          <TableCell className="font-medium">
                            {n.numero ? `${n.numero}/${n.serie}` : '-'}
                            {n.ambiente === 'homologacao' && (
                              <span className="ml-2 text-[10px] uppercase text-muted-foreground">teste</span>
                            )}
                          </TableCell>
                          <TableCell>{new Date(n.emitida_em || n.created_at).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell>
                            <div>{n.destinatario?.nome || '-'}</div>
                            <div className="text-xs text-muted-foreground">
                              {formatarDocumento(n.destinatario?.cnpj || n.destinatario?.cpf)}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{formatarMoeda(n.valor_total)}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_NOTA_CLASSE[n.status]}>{STATUS_NOTA_LABEL[n.status]}</Badge>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" title="Usar como base para nova NF-e" onClick={() => usarComoBase(n.id)}>
                              <Copy className="h-4 w-4" />
                            </Button>
                            {n.danfe_url && (
                              <Button variant="ghost" size="sm" asChild>
                                <a href={n.danfe_url} target="_blank" rel="noopener noreferrer">
                                  <FileText className="h-4 w-4 mr-1" /> DANFE
                                </a>
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="produtos">
          <ProdutosRevendaTab
            produtos={produtos}
            regras={regras}
            carregando={carregandoProdutos}
            onAlterado={carregarCadastros}
          />
        </TabsContent>

        <TabsContent value="configuracao">
          <ConfiguracaoFiscalTab config={config} status={status} regras={regras} onAlterado={carregarCadastros} />
        </TabsContent>
      </Tabs>

      <NotaFiscalFormDialog
        open={formAberto}
        onOpenChange={setFormAberto}
        notaId={notaEditando}
        baseId={notaBase}
        ambiente={ambiente}
        produtos={produtosAtivos}
        regras={regras}
        onSalva={aoSalvar}
      />

      <NotaFiscalDetalheDialog
        notaId={notaDetalhe}
        onOpenChange={(open) => !open && setNotaDetalhe(null)}
        onAtualizada={aoAtualizarNota}
        onEditar={abrirEdicao}
        onUsarComoBase={usarComoBase}
      />
    </div>
  );
}
